/**
 * Exact-text file editing for `wfs-edit`.
 *
 * Ported from earendil-works/pi (MIT license):
 * - packages/coding-agent/src/core/tools/edit-diff.ts at 1355cd36e0b10a3e71c6c78f713b7b36458db27f
 * - packages/coding-agent/src/core/tools/edit.ts at fcff255b004a6cde812b5b7a714e0bfe9c540986
 *   (prepareEditArguments, validateEditInput)
 * - packages/coding-agent/src/utils/text.ts at fcff255b004a6cde812b5b7a714e0bfe9c540986
 *   (splitBom)
 *
 * Matching follows pi (exact first, then a fuzzy fallback) with deliberate
 * deviations:
 * - Failures throw `WfsEditError` carrying a stable `code`, so `wfs-edit`
 *   can return it and `agent_service.tool_error` breaks down by reason.
 * - Occurrence checks prefer an exact count: a unique exact match is applied
 *   even when a fuzzy twin exists, because quoting more of the same
 *   normalized context cannot disambiguate it.
 * - Fuzzy matching only locates the span; the replacement splices `newText`
 *   over the original bytes via a source-offset map. pi instead rewrites
 *   every touched line from normalized text, which silently folds typography
 *   the model never quoted (curly quotes, dashes). Each edit also resolves
 *   in its own space, so one fuzzy edit cannot push an exact edit into
 *   fuzzy counting.
 * - An edit that replaces text with itself is rejected before matching:
 *   fuzzy locating would otherwise rewrite the matched span (e.g. folding
 *   the file's curly quotes to the model's ASCII quotes) and report success.
 * - Occurrence counts include overlapping matches: disjoint counting would
 *   accept `ana` in `banana` as unique instead of asking for disambiguation.
 * - Fuzzy matches that start or end inside one source character's NFKC
 *   output are rejected: mapping the span back would delete letters the
 *   replacement did not quote.
 *
 * Pure functions - no I/O, no external dependencies.
 */

/** Stable failure codes for `wfs-edit`, surfaced as `errorType` on `agent_service.tool_error`. */
export type WfsEditErrorCode =
  | 'WFS_EDIT_INVALID_INPUT'
  | 'WFS_EDIT_EMPTY_OLD_TEXT'
  | 'WFS_EDIT_NOT_FOUND'
  | 'WFS_EDIT_PARTIAL_CHAR'
  | 'WFS_EDIT_AMBIGUOUS'
  | 'WFS_EDIT_OVERLAP'
  | 'WFS_EDIT_NO_OP';

/**
 * Error thrown when an exact-text edit cannot be applied.
 *
 * The message is pi's message verbatim (the model reads it); the code lets
 * metrics and the Datadog failure taxonomy classify the failure.
 */
export class WfsEditError extends Error {
  constructor(
    public readonly code: WfsEditErrorCode,
    message: string
  ) {
    super(message);
    this.name = 'WfsEditError';
  }
}

export interface Edit {
  oldText: string;
  newText: string;
}

/**
 * Detect which line ending to restore after editing.
 *
 * First match wins, not majority: returns `'\r\n'` when the first CRLF
 * precedes the first bare LF, else `'\n'`. A file that starts with one CRLF
 * and is otherwise LF gets fully rewritten to CRLF on write; materialized
 * files are consistent in practice, so this stays a documented edge.
 *
 * @param content - File content to inspect.
 * @returns The detected line ending.
 */
export function detectLineEnding(content: string): '\r\n' | '\n' {
  const crlfIdx = content.indexOf('\r\n');
  const lfIdx = content.indexOf('\n');
  if (lfIdx === -1) return '\n';
  if (crlfIdx === -1) return '\n';
  return crlfIdx < lfIdx ? '\r\n' : '\n';
}

/**
 * Normalize all line endings to LF.
 *
 * @param text - Text to normalize.
 * @returns The text with `\r\n` and bare `\r` replaced by `\n`.
 */
export function normalizeToLF(text: string): string {
  return text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
}

/**
 * Restore the original line ending style after editing.
 *
 * @param text - LF-normalized text.
 * @param ending - The ending detected before matching.
 * @returns The text with `\n` replaced by `\r\n` when the original used CRLF.
 */
export function restoreLineEndings(
  text: string,
  ending: '\r\n' | '\n'
): string {
  return ending === '\r\n' ? text.replace(/\n/g, '\r\n') : text;
}

export interface NormalizedText {
  /** Fuzzy-normalized text. */
  text: string;
  /**
   * Source UTF-16 offset for each UTF-16 offset in `text`, so a span found
   * by fuzzy matching maps back to the original bytes it came from.
   */
  sourceOffsets: number[];
}

/**
 * Fold one UTF-16 unit the way fuzzy normalization folds it.
 *
 * Covers pi's single-character maps: smart quotes to ASCII quotes, Unicode
 * dashes to hyphen, special spaces to a regular space.
 *
 * @param unit - One UTF-16 unit.
 * @returns The folded unit, or the unit unchanged.
 */
function foldCharForFuzzyMatch(unit: string): string {
  if (unit.length !== 1) return unit;
  const code = unit.charCodeAt(0);
  // Smart single quotes -> '
  if (code === 0x2018 || code === 0x2019 || code === 0x201a || code === 0x201b)
    return "'";
  // Smart double quotes -> "
  if (code === 0x201c || code === 0x201d || code === 0x201e || code === 0x201f)
    return '"';
  // Various dashes/hyphens -> -
  // U+2010 hyphen, U+2011 non-breaking hyphen, U+2012 figure dash,
  // U+2013 en-dash, U+2014 em-dash, U+2015 horizontal bar, U+2212 minus
  if (
    code === 0x2010 ||
    code === 0x2011 ||
    code === 0x2012 ||
    code === 0x2013 ||
    code === 0x2014 ||
    code === 0x2015 ||
    code === 0x2212
  )
    return '-';
  // Special spaces -> regular space
  // U+00A0 NBSP, U+2002-U+200A various spaces, U+202F narrow NBSP,
  // U+205F medium math space, U+3000 ideographic space
  if (
    code === 0x00a0 ||
    (code >= 0x2002 && code <= 0x200a) ||
    code === 0x202f ||
    code === 0x205f ||
    code === 0x3000
  )
    return ' ';
  return unit;
}

/**
 * Normalize text for fuzzy matching, tracking where each output unit came from.
 *
 * Applies pi's transformations (NFKC, per-line trailing whitespace
 * stripping, smart quote/dash/space folding to ASCII) while recording the
 * source UTF-16 offset of every output unit.
 *
 * NFKC runs per code point rather than over the whole string so the mapping
 * stays exact; the output differs from whole-string NFKC only when
 * composition crosses characters (combining marks), which is vanishingly
 * rare in authored files.
 *
 * @param text - LF-normalized text to normalize.
 * @returns The normalized text plus per-unit source offsets.
 */
export function normalizeForFuzzyMatchWithMap(text: string): NormalizedText {
  // Phase 1: per-code-point NFKC, tracking UTF-16 source offsets (an
  // expanded char maps every output unit back to its source offset).
  const units: string[] = [];
  const sources: number[] = [];
  for (let i = 0; i < text.length; ) {
    const char = String.fromCodePoint(text.codePointAt(i)!);
    const folded = char.normalize('NFKC');
    for (let k = 0; k < folded.length; k++) {
      units.push(folded[k]!);
      sources.push(i);
    }
    i += char.length;
  }

  // Phase 2: per-line trimEnd and 1:1 folds.
  const outUnits: string[] = [];
  const outSources: number[] = [];
  let lineStart = 0;
  const flushLine = (lineEnd: number, newlineSource: number | null): void => {
    let end = lineEnd;
    while (end > lineStart && /\s/.test(units[end - 1]!)) end--;
    for (let j = lineStart; j < end; j++) {
      outUnits.push(foldCharForFuzzyMatch(units[j]!));
      outSources.push(sources[j]!);
    }
    if (newlineSource !== null) {
      outUnits.push('\n');
      outSources.push(newlineSource);
    }
  };
  for (let j = 0; j < units.length; j++) {
    if (units[j] === '\n') {
      flushLine(j, sources[j]!);
      lineStart = j + 1;
    }
  }
  flushLine(units.length, null);
  return {text: outUnits.join(''), sourceOffsets: outSources};
}

/**
 * Normalize text for fuzzy matching. Applies progressive transformations:
 * - Strip trailing whitespace from each line
 * - Normalize smart quotes to ASCII equivalents
 * - Normalize Unicode dashes/hyphens to ASCII hyphen
 * - Normalize special Unicode spaces to regular space
 */
export function normalizeForFuzzyMatch(text: string): string {
  return normalizeForFuzzyMatchWithMap(text).text;
}

/**
 * Whether a normalized match starts or ends inside one source character's
 * normalized output (an NFKC expansion, or the second unit of a surrogate
 * pair).
 *
 * A run of normalized units sharing one source offset is exactly one source
 * code point's output, so a same-source neighbor outside the span means the
 * boundary falls mid-character. Mapping such a span back would widen the
 * replacement over unquoted text, so callers must reject the match instead
 * of applying it.
 *
 * @param sourceOffsets - Source map from `normalizeForFuzzyMatchWithMap`.
 * @param normStart - Match start offset in normalized text.
 * @param normLength - Match length in normalized text (non-zero).
 * @returns True when either match boundary splits a source character.
 */
function splitsSourceChar(
  sourceOffsets: number[],
  normStart: number,
  normLength: number
): boolean {
  if (
    normStart > 0 &&
    sourceOffsets[normStart - 1] === sourceOffsets[normStart]
  ) {
    return true;
  }
  const normEnd = normStart + normLength;
  return (
    normEnd < sourceOffsets.length &&
    sourceOffsets[normEnd] === sourceOffsets[normEnd - 1]
  );
}

/**
 * Map a span found in normalized text back to the original bytes it came from.
 *
 * The span must cover whole source characters (see `splitsSourceChar`):
 * mapping is character-granular, so a match starting or ending inside one
 * source character's normalized output would widen the replacement over
 * unquoted text.
 *
 * @param content - Original LF-normalized content.
 * @param sourceOffsets - Source map from `normalizeForFuzzyMatchWithMap`.
 * @param normStart - Match start offset in normalized text.
 * @param normLength - Match length in normalized text (non-zero).
 * @returns The `[start, end)` span in original UTF-16 offsets.
 */
function mapNormalizedSpanToOriginal(
  content: string,
  sourceOffsets: number[],
  normStart: number,
  normLength: number
): {start: number; end: number} {
  const start = sourceOffsets[normStart]!;
  const lastSource = sourceOffsets[normStart + normLength - 1]!;
  const lastCharLength = String.fromCodePoint(
    content.codePointAt(lastSource)!
  ).length;
  return {start, end: lastSource + lastCharLength};
}

interface MatchedEdit {
  editIndex: number;
  matchIndex: number;
  matchLength: number;
  newText: string;
}

type TextReplacement = Pick<
  MatchedEdit,
  'matchIndex' | 'matchLength' | 'newText'
>;

/**
 * Splice replacements into content from last to first so offsets stay valid.
 *
 * @param content - The content to rewrite.
 * @param replacements - Replacements with offsets into `content`.
 * @returns The content with every replacement applied.
 */
function applyReplacements(
  content: string,
  replacements: TextReplacement[]
): string {
  let result = content;
  for (let i = replacements.length - 1; i >= 0; i--) {
    const replacement = replacements[i]!;
    result =
      result.substring(0, replacement.matchIndex) +
      replacement.newText +
      result.substring(replacement.matchIndex + replacement.matchLength);
  }
  return result;
}

export interface AppliedEditsResult {
  baseContent: string;
  newContent: string;
}

/**
 * Count occurrences of a needle, including overlapping ones.
 *
 * `split()`-style counting only finds disjoint matches, so `ana` in `banana`
 * would count as one and pass the unique-match check. Advancing from each
 * match's start plus one keeps the count exact.
 *
 * @param content - Content to search.
 * @param needle - Needle to count; empty counts as zero.
 * @returns The number of occurrences, including overlaps.
 */
function countOverlappingOccurrences(content: string, needle: string): number {
  if (needle.length === 0) return 0;
  let count = 0;
  let from = content.indexOf(needle);
  while (from !== -1) {
    count++;
    from = content.indexOf(needle, from + 1);
  }
  return count;
}

/**
 * Count how many times an already-normalized needle occurs in
 * already-normalized content, including overlapping matches.
 *
 * @param fuzzyContent - Normalized content to search.
 * @param fuzzyNeedle - Normalized needle (non-empty; callers reject empty
 * fuzzy needles first).
 * @returns The number of normalized occurrences.
 */
function countNormalizedOccurrences(
  fuzzyContent: string,
  fuzzyNeedle: string
): number {
  return countOverlappingOccurrences(fuzzyContent, fuzzyNeedle);
}

/**
 * Count exact occurrences of oldText, including overlapping ones.
 *
 * Preferred over the fuzzy count when it is non-zero. A unique exact match
 * must not be refused because a smart-quote or trailing-whitespace twin
 * normalizes to the same string; extra quoted context cannot separate those.
 *
 * @param content - Content to search.
 * @param oldText - Non-empty needle.
 * @returns The number of exact occurrences.
 */
function countExactOccurrences(content: string, oldText: string): number {
  return countOverlappingOccurrences(content, oldText);
}

/**
 * Whether oldText cannot identify a span, including after fuzzy normalization.
 *
 * `normalizeForFuzzyMatch` trims each line, so spaces or tabs become `""` and
 * `indexOf("")` reports a match at offset 0. A needle that only survives as
 * newlines (spaces around a line break) matches a real line break the same
 * way. An oldText that is already only newlines is a blank-line edit and is
 * not empty.
 *
 * @param oldText - LF-normalized old text.
 * @returns True when the edit should fail as `WFS_EDIT_EMPTY_OLD_TEXT`.
 */
function isEffectivelyEmptyOldText(oldText: string): boolean {
  if (oldText.length === 0) return true;
  const fuzzy = normalizeForFuzzyMatch(oldText);
  if (fuzzy.length === 0) return true;
  if (fuzzy.replace(/\n/g, '').length > 0) return false;
  return oldText.replace(/\n/g, '').length > 0;
}

/**
 * Build the error for an edit whose oldText matches nowhere.
 *
 * @param path - File path for the message.
 * @param editIndex - Index of the failing edit.
 * @param totalEdits - Total edits in the call (selects the message variant).
 * @returns A `WfsEditError` with pi's message.
 */
function getNotFoundError(
  path: string,
  editIndex: number,
  totalEdits: number
): WfsEditError {
  if (totalEdits === 1) {
    return new WfsEditError(
      'WFS_EDIT_NOT_FOUND',
      `Could not find the exact text in ${path}. The old text must match exactly including all whitespace and newlines.`
    );
  }
  return new WfsEditError(
    'WFS_EDIT_NOT_FOUND',
    `Could not find edits[${editIndex}] in ${path}. The oldText must match exactly including all whitespace and newlines.`
  );
}

/**
 * Build the error for a fuzzy match that splits a source character.
 *
 * Same model-facing message as not-found (quoting the complete character
 * yields an exact match), but a dedicated code so metrics distinguish
 * "needle isn't in the file" from "located but unsafe to apply".
 *
 * @param path - File path for the message.
 * @param editIndex - Index of the failing edit.
 * @param totalEdits - Total edits in the call (selects the message variant).
 * @returns A `WfsEditError` with the not-found message and partial-char code.
 */
function getPartialCharError(
  path: string,
  editIndex: number,
  totalEdits: number
): WfsEditError {
  const notFound = getNotFoundError(path, editIndex, totalEdits);
  return new WfsEditError('WFS_EDIT_PARTIAL_CHAR', notFound.message);
}

/**
 * Build the error for an edit whose oldText matches more than once.
 *
 * @param path - File path for the message.
 * @param editIndex - Index of the failing edit.
 * @param totalEdits - Total edits in the call (selects the message variant).
 * @param occurrences - How many times oldText matched.
 * @returns A `WfsEditError` with pi's message.
 */
function getDuplicateError(
  path: string,
  editIndex: number,
  totalEdits: number,
  occurrences: number
): WfsEditError {
  if (totalEdits === 1) {
    return new WfsEditError(
      'WFS_EDIT_AMBIGUOUS',
      `Found ${occurrences} occurrences of the text in ${path}. The text must be unique. Please provide more context to make it unique.`
    );
  }
  return new WfsEditError(
    'WFS_EDIT_AMBIGUOUS',
    `Found ${occurrences} occurrences of edits[${editIndex}] in ${path}. Each oldText must be unique. Please provide more context to make it unique.`
  );
}

/**
 * Build the error for an edit with an empty oldText.
 *
 * A whitespace-only needle gets its own message: it names what the model
 * actually sent, which pi's verbatim 'must not be empty' contradicts.
 *
 * @param path - File path for the message.
 * @param editIndex - Index of the failing edit.
 * @param totalEdits - Total edits in the call (selects the message variant).
 * @param whitespaceOnly - True when oldText is non-empty but effectively empty.
 * @returns A `WfsEditError` with pi's message, or the whitespace variant.
 */
function getEmptyOldTextError(
  path: string,
  editIndex: number,
  totalEdits: number,
  whitespaceOnly = false
): WfsEditError {
  const reason = whitespaceOnly
    ? 'must not be empty or whitespace-only'
    : 'must not be empty';
  if (totalEdits === 1) {
    return new WfsEditError(
      'WFS_EDIT_EMPTY_OLD_TEXT',
      `oldText ${reason} in ${path}.`
    );
  }
  return new WfsEditError(
    'WFS_EDIT_EMPTY_OLD_TEXT',
    `edits[${editIndex}].oldText ${reason} in ${path}.`
  );
}

/**
 * Build the error for edits that leave the content unchanged.
 *
 * A pre-match refusal names the self-identical edit: the precise cause the
 * code already knows beats pi's generic wording, which would otherwise nudge
 * the model toward "the file already has the desired content".
 *
 * @param path - File path for the message.
 * @param totalEdits - Total edits in the call (selects the message variant).
 * @param editIndex - Index of the self-identical edit, when the refusal
 * happens before matching and names the offending edit.
 * @returns A `WfsEditError`: the self-identical wording (with the edit index
 * when the call has multiple edits) for a pre-match refusal, otherwise pi's
 * post-apply message.
 */
function getNoChangeError(
  path: string,
  totalEdits: number,
  editIndex?: number
): WfsEditError {
  if (editIndex !== undefined) {
    return new WfsEditError(
      'WFS_EDIT_NO_OP',
      totalEdits === 1
        ? `No changes requested in ${path}: oldText and newText are identical.`
        : `No changes requested by edits[${editIndex}] in ${path}: oldText and newText are identical.`
    );
  }
  if (totalEdits === 1) {
    return new WfsEditError(
      'WFS_EDIT_NO_OP',
      `No changes made to ${path}. The replacement produced identical content. This might indicate an issue with special characters or the text not existing as expected.`
    );
  }
  return new WfsEditError(
    'WFS_EDIT_NO_OP',
    `No changes made to ${path}. The replacements produced identical content.`
  );
}

/**
 * Apply one or more exact-text replacements to LF-normalized content.
 *
 * All edits are matched against the same original content, and every match
 * resolves to original byte offsets, so replacements splice `newText` over
 * exactly the quoted span and nothing else on the line changes. Each edit
 * resolves in its own space: a unique exact match applies even when another
 * edit in the same call only matches fuzzily. Fuzzy matching only locates
 * the span; the span is mapped back to the original bytes before replacing.
 * Replacements apply in reverse order so offsets remain stable.
 */
export function applyEditsToNormalizedContent(
  normalizedContent: string,
  edits: Edit[],
  path: string
): AppliedEditsResult {
  const normalizedEdits = edits.map((edit) => ({
    oldText: normalizeToLF(edit.oldText),
    newText: normalizeToLF(edit.newText),
  }));

  for (let i = 0; i < normalizedEdits.length; i++) {
    const oldText = normalizedEdits[i]!.oldText;
    if (isEffectivelyEmptyOldText(oldText)) {
      throw getEmptyOldTextError(
        path,
        i,
        normalizedEdits.length,
        oldText.length > 0
      );
    }
  }

  // An edit that replaces text with itself asks for no change, but fuzzy
  // locating would still rewrite the matched span (e.g. folding the file's
  // curly quotes to the ASCII quotes the model sent) and report success.
  // Fail closed before matching when an edit asks for nothing.
  const noOpIndex = normalizedEdits.findIndex(
    (edit) => edit.oldText === edit.newText
  );
  if (noOpIndex !== -1) {
    throw getNoChangeError(path, normalizedEdits.length, noOpIndex);
  }

  // Normalized once with its source map; shared by every edit that falls
  // back to fuzzy locating below.
  const {text: fuzzyContent, sourceOffsets} =
    normalizeForFuzzyMatchWithMap(normalizedContent);

  const matchedEdits: MatchedEdit[] = [];
  for (let i = 0; i < normalizedEdits.length; i++) {
    const edit = normalizedEdits[i]!;
    const exactOccurrences = countExactOccurrences(
      normalizedContent,
      edit.oldText
    );
    if (exactOccurrences === 1) {
      matchedEdits.push({
        editIndex: i,
        matchIndex: normalizedContent.indexOf(edit.oldText),
        matchLength: edit.oldText.length,
        newText: edit.newText,
      });
      continue;
    }
    if (exactOccurrences > 1) {
      throw getDuplicateError(
        path,
        i,
        normalizedEdits.length,
        exactOccurrences
      );
    }
    const fuzzyNeedle = normalizeForFuzzyMatch(edit.oldText);
    const fuzzyIndex = fuzzyContent.indexOf(fuzzyNeedle);
    if (fuzzyIndex === -1) {
      throw getNotFoundError(path, i, normalizedEdits.length);
    }
    const fuzzyOccurrences = countNormalizedOccurrences(
      fuzzyContent,
      fuzzyNeedle
    );
    if (fuzzyOccurrences > 1) {
      throw getDuplicateError(
        path,
        i,
        normalizedEdits.length,
        fuzzyOccurrences
      );
    }
    if (splitsSourceChar(sourceOffsets, fuzzyIndex, fuzzyNeedle.length)) {
      throw getPartialCharError(path, i, normalizedEdits.length);
    }
    const {start, end} = mapNormalizedSpanToOriginal(
      normalizedContent,
      sourceOffsets,
      fuzzyIndex,
      fuzzyNeedle.length
    );
    matchedEdits.push({
      editIndex: i,
      matchIndex: start,
      matchLength: end - start,
      newText: edit.newText,
    });
  }

  matchedEdits.sort((a, b) => a.matchIndex - b.matchIndex);
  for (let i = 1; i < matchedEdits.length; i++) {
    const previous = matchedEdits[i - 1]!;
    const current = matchedEdits[i]!;
    if (previous.matchIndex + previous.matchLength > current.matchIndex) {
      throw new WfsEditError(
        'WFS_EDIT_OVERLAP',
        `edits[${previous.editIndex}] and edits[${current.editIndex}] overlap in ${path}. Merge them into one edit or target disjoint regions.`
      );
    }
  }

  const baseContent = normalizedContent;
  const newContent = applyReplacements(normalizedContent, matchedEdits);

  if (baseContent === newContent) {
    throw getNoChangeError(path, normalizedEdits.length);
  }

  return {baseContent, newContent};
}

/** Split a leading UTF-8 byte order mark from decoded text. */
export function splitBom(content: string): {bom: string; text: string} {
  return content.startsWith('\uFEFF')
    ? {bom: '\uFEFF', text: content.slice(1)}
    : {bom: '', text: content};
}

export interface WfsEditToolInput {
  path: string;
  edits: Edit[];
}

type LegacyEditToolInput = WfsEditToolInput & {
  oldText?: unknown;
  newText?: unknown;
};

type SingleEditInput = {oldText: string; newText: string};

/**
 * Check whether a value is a single `{oldText, newText}` edit object.
 *
 * @param value - Value to check.
 * @returns True when the value has string `oldText` and `newText`.
 */
function isSingleEditInput(value: unknown): value is SingleEditInput {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }

  const edit = value as Record<string, unknown>;
  return typeof edit.oldText === 'string' && typeof edit.newText === 'string';
}

/**
 * Repair the shapes models actually send into `{path, edits[]}`.
 *
 * Accepts `edits` as a JSON string, a single edit object, or legacy top-level
 * `oldText`/`newText`, matching pi's `prepareArguments` behavior.
 *
 * @param input - Raw tool input of unknown shape.
 * @returns The input with `edits` normalized to an array.
 */
export function prepareEditArguments(input: unknown): WfsEditToolInput {
  if (!input || typeof input !== 'object') {
    return input as WfsEditToolInput;
  }

  const args: Record<string, unknown> = {
    ...(input as Record<string, unknown>),
  };

  // Some models (Opus 4.6, GLM-5.1) send edits as a JSON string instead of an array.
  // Others send a single edit object instead of a one-element edits array.
  if (typeof args.edits === 'string') {
    try {
      const parsed: unknown = JSON.parse(args.edits);
      if (Array.isArray(parsed)) {
        args.edits = parsed;
      } else if (isSingleEditInput(parsed)) {
        args.edits = [parsed];
      }
    } catch {
      // Leave unparseable strings for schema validation to reject.
    }
  } else if (isSingleEditInput(args.edits)) {
    args.edits = [args.edits];
  }

  const legacy = args as unknown as LegacyEditToolInput;
  if (
    typeof legacy.oldText !== 'string' ||
    typeof legacy.newText !== 'string'
  ) {
    return args as unknown as WfsEditToolInput;
  }

  const edits = Array.isArray(legacy.edits) ? [...legacy.edits] : [];
  edits.push({oldText: legacy.oldText, newText: legacy.newText});
  const {oldText: _oldText, newText: _newText, ...rest} = legacy;
  return {...rest, edits} as WfsEditToolInput;
}

/**
 * Validate repaired edit input before matching.
 *
 * @param input - Repaired tool input.
 * @returns The path and the non-empty edits array.
 */
export function validateEditInput(input: WfsEditToolInput): {
  path: string;
  edits: Edit[];
} {
  if (!Array.isArray(input.edits) || input.edits.length === 0) {
    throw new WfsEditError(
      'WFS_EDIT_INVALID_INPUT',
      'Edit tool input is invalid. edits must contain at least one replacement.'
    );
  }
  return {path: input.path, edits: input.edits};
}
